import clsx from 'clsx';
import {
  forwardRef,
  useMemo,
  ComponentPropsWithRef,
  MouseEventHandler as ReactMouseEventHandler,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';

import * as itemStyles from './CalendarItem.sty.js';
import { Button } from '../../../buttons/button/Button.js';
import { useFormFieldContext } from '../../forms-core/hooks/useFormFieldContext.js';
import { useCalendarContext } from '../hooks/useCalendarContext.js';
import type { CalendarViewType } from '../types/shared-types.js';

//#region types
/** @internal */
interface CalendarButtonProps extends ComponentPropsWithRef<'div'> {
  /** Controls the disabled state. */
  disabled?: boolean;
  /** Whether the calendar button is focused. */
  focused?: boolean;
}

type CalendarDayButtonProps = {
  isCurrentMonthDay?: boolean;
  isOnlySelected?: boolean;
};

/** @internal */
export interface ViewItem {
  /** The localized aria label of the view item. */
  ariaLabel: string;
  /** The tabindex attribute of the view item. */
  tabIndex: 0 | -1;
  /** Whether this is the current view item (i.e. today, current month, current year). */
  isCurrent: boolean;
  /** Whether the item is disabled. */
  isDisabled?: boolean;
  /** Color used for the highlighting. */
  highlightColor?: 'neutral' | 'primary' | 'critical';
  /** Whether the view item is actually selected. */
  isSelected?: boolean;
  /** Whether a complete date or timeframe was selected. */
  isIncompleteSelection?: boolean;
  /** Event listener for clicks on the view item. */
  onClick: () => void;
  /** Event listener for complex hover styles on range selection. */
  rangeInteractionProps?: {
    onMouseEnter?: ReactMouseEventHandler<HTMLElement>;
    onMouseLeave?: ReactMouseEventHandler<HTMLElement>;
  };
  /** How the item should be highlighted. */
  highlightType?: 'first' | 'last' | 'inbetween' | 'same';
  /** What kind of selection outline should be used. */
  selectionVariant?:
    | 'absolute'
    | 'relative'
    | 'absolute-relative'
    | 'relative-absolute';
}

/** @internal */
type CalendarItemProps = ViewItem &
  CalendarButtonProps & {
    isInRange?: boolean;
    view: CalendarViewType;
  } & CalendarDayButtonProps;
//#endregion

/** @internal */
export const CalendarItem = /* @__PURE__ */ forwardRef(
  (
    {
      children,
      isCurrent = false,
      highlightColor = 'neutral',
      onClick,
      isSelected = false,
      isDisabled = false,
      isCurrentMonthDay = true,
      view,
      ariaLabel,
      ...remainingProps
    }: CalendarItemProps,
    ref: typeof remainingProps.ref,
  ) => {
    const { disabled, ariaDisabled } = useFormFieldContext();
    const { readOnly } = useCalendarContext();

    const variant = useMemo(() => {
      // The day doesn't rely on the button background color styles.
      if (view === 'day') {
        return 'default';
      }

      if (isSelected) {
        return 'accent';
      }

      if (isCurrent) {
        return 'emphasized';
      }

      return 'default';
    }, [view, isSelected, isCurrent]);

    const currentStyle =
      view !== 'day' && isCurrent ? itemStyles.currentIndicator : '';

    const selectionHandler =
      !isDisabled && !disabled && !readOnly
        ? {
            onClick,
            onKeyDown: (event: ReactKeyboardEvent<HTMLDivElement>) => {
              const { code } = event;

              if (code === 'Space' || code === 'Enter') {
                event.preventDefault();
                onClick?.();
              }
            },
          }
        : {};

    return (
      <Button
        as="div"
        ref={ref}
        aria-label={ariaLabel}
        {...remainingProps}
        className={clsx(itemStyles.calendarButton, currentStyle, {
          [itemStyles.outOfScopeDay]:
            !isCurrentMonthDay &&
            highlightColor === 'neutral' &&
            !(disabled || isDisabled || ariaDisabled),
        })}
        color={highlightColor}
        disabled={disabled || isDisabled}
        aria-disabled={ariaDisabled}
        variant={variant}
        {...selectionHandler}
      >
        {children}
      </Button>
    );
  },
);

(
  CalendarItem as typeof CalendarItem & {
    displayName: string;
  }
).displayName = 'CalendarBodyDateButton';
